import { useState } from "react";
import { styled } from "styled-components";
import { AuthenticationState } from "./domain/AuthenticationState";
import Initializing from "./initializing/Initializing";
import Authenticating, {
  AuthenticatingText,
} from "./authenticating/Authenticating";
import Confirming from "./confirming/Confirming";
import { RedirectText } from "../RedirectComponent";
import { UseCase } from "../base/UseCase";
import ProcessContextBlock, {
  ClientConsentContext,
} from "../base/ProcessContextBlock";
import { ClientNameBoxProps } from "../base/ClientNameBox";
import { ProcessStateImageProps } from "../base/ProcessStateImage";
import Authenticated, {
  AuthenticatedText,
} from "./authenticated/Authenticated";

function authenticationStateHandler(
  state: AuthenticationState,
  useCase: UseCase,
  isDesktop: boolean,
  identityAttributes: Array<string>,
  authenticationText: AuthenticationTextFields,
  showClientContext: (visibile: boolean) => void,
  displayClientConsentContext: (display: boolean) => void,
  clientName: string | undefined,
  clientContextText: string | undefined,
  noAuthenticationSpinner: boolean,
): JSX.Element {
  switch (state.type) {
    case "ChallengesRequested":
      return <Initializing />;
    case "ShowChallenges":
      return (
        <Authenticating
          clientName={clientName}
          clientContextText={clientContextText}
          ckChallenge={state.ckChallenge}
          isDesktop={isDesktop}
          identityAttributes={identityAttributes}
          t={authenticationText.authenticating}
          showClientContext={showClientContext}
          displayClientConsentContext={displayClientConsentContext}
          noAuthenticationSpinner={noAuthenticationSpinner}
          useCase={useCase}
        />
      );

    case "Authenticated":
      return (
        <Confirming
          showNonPKIoAlert={state.showNonPKIoAlert}
          t={{
            redirect: authenticationText.confirming.redirecting,
          }}
          clientName={clientName}
          redirectToUri={state.redirectUri}
          useCase={useCase}
          isDesktop={isDesktop}
        />
      );
    default:
      return <></>;
  }
}
export interface AuthenticationComponentProps {
  relyingPartyName?: string | undefined;
  clientContextText?: string | undefined;
  title: string;
  logoUri?: string | undefined;
  contextText?: string | undefined;
  identityAttributes: Array<string>;
  state: AuthenticationState;
  useCase: UseCase;
  authenticationText: AuthenticationTextFields;
  isDesktop: boolean;
  redirectToUri?: string | undefined;
  noAuthenticationSpinner: boolean;
}

export interface AuthenticationTextFields {
  authenticating: AuthenticatingText;
  confirming: {
    redirecting: RedirectText;
    nonPKIoWarning: string;
  };
  authenticated: AuthenticatedText;
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-height: 1px;
  height: auto;
  gap: 24px;
  background: transparent;
  border-radius: 0;
  box-sizing: border-box;
`;

const StateBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default function AuthenticationComponent({
  relyingPartyName,
  clientContextText,
  title,
  logoUri,
  state,
  authenticationText,
  isDesktop,
  identityAttributes,
  noAuthenticationSpinner,
  useCase,
}: AuthenticationComponentProps): JSX.Element {
  const [showClientContext, setShowClientContext] = useState<boolean>(true);
  const [displayClientConsentContext, toggleDisplayClientConsentContext] =
    useState<boolean>(false);

  function setClientNameBox(
    clientName: string | undefined,
    clientLogo: string | undefined,
    display: boolean,
  ): ClientNameBoxProps | undefined {
    if (clientName !== undefined) {
      return { clientName, clientLogo, display };
    } else {
      return undefined;
    }
  }

  function setClientContext({
    useCase,
    state,
    headerText,
    clientContextText,
  }: {
    useCase: UseCase;
    state: AuthenticationState;
    headerText: string;
    clientContextText: string | undefined;
  }): ClientConsentContext | undefined {
    switch (useCase) {
      case "HashSigning":
        return undefined;
      case "EasyLogin":
      case "Identification":
        switch (state.type) {
          case "ChallengesRequested":
            return undefined;
          case "ShowChallenges":
            return {
              clientContextHeaderText: headerText,
              clientContextText: clientContextText,
            };
          case "Authenticated":
            return undefined;
          case "AlreadyAuthenticated":
            return undefined;
        }
    }
  }

  function setProcessStateImage(
    useCase: UseCase,
    state: AuthenticationState,
  ): ProcessStateImageProps | undefined {
    switch (useCase) {
      case "HashSigning": {
        switch (state.type) {
          case "ChallengesRequested":
            return undefined;
          case "ShowChallenges":
            return { state: { type: "Authenticating" } };
          case "Authenticated":
            return { state: { type: "Authenticated" } };
          case "AlreadyAuthenticated":
            return { state: { type: "Authenticated" } };
        }
      }
      case "EasyLogin":
      case "Identification": {
        return undefined;
      }
    }
  }

  if (state.type !== "AlreadyAuthenticated") {
    return (
      <>
        <ContentStack>
          <ProcessContextBlock
            title={title}
            isDesktop={isDesktop}
            displayClientConsentContext={displayClientConsentContext}
            clientNameBox={setClientNameBox(
              relyingPartyName,
              logoUri,
              showClientContext,
            )}
            clientConsentContext={setClientContext({
              useCase,
              state,
              headerText:
                authenticationText.authenticating.clientContextHeaderText,
              clientContextText,
            })}
            processStateImage={setProcessStateImage(useCase, state)}
          />
          <StateBox>
            {authenticationStateHandler(
              state,
              useCase,
              isDesktop,
              identityAttributes,
              authenticationText,
              setShowClientContext,
              toggleDisplayClientConsentContext,
              relyingPartyName,
              clientContextText,
              noAuthenticationSpinner,
            )}
          </StateBox>
        </ContentStack>
      </>
    );
  } else {
    return <Authenticated t={authenticationText.authenticated} />;
  }
}
